import { AnykFidHely, AnykSzabalymotor } from './anyk-szabalymotor';
import { NyMezo, NyModell } from './nav-nyomtatvany.service';
import { AnykHely } from './anyk-lap/anyk-lap.component';

/**
 *  ÁNYK-LAPKÉP ↔ BEVALLÁSADAT KÖTÉS.
 *
 *  A nyomtatványkép mezőit az ÁNYK-sablon azonosítója (fid) jelöli, az adat viszont az XSD
 *  szerkezetében áll (részbizonylat → blokk → lánc sorai → mező). Ez az osztály a kettő közti
 *  leképezés: melyik fid melyik adatmezőt jelenti, hány példánya van egy dinamikus lapnak, és
 *  mely lapok kitöltöttek – nyomtatáskor csak azokat visszük papírra.
 *
 *  A kitöltő (kitolto.component) és a kötegelt nyomtatás ugyanezt használja: a leképezés egy
 *  helyen él, hogy a papírkép és a képernyő ne csúszhasson el egymástól.
 */
export class AnykKotes {
  /** Formonként: fid → XSD-mező és a láncok, amelyekben áll. */
  private xsdIdx: Record<string, Record<string, { m: NyMezo; lancok: string[] }>> = {};
  private lapLancTar = new Map<string, string | null>();
  private helyTar = new Map<string, AnykFidHely | null>();
  private motorLapTar = new Map<string, { nev: string; laponkent: number } | null>();

  constructor(private modell: NyModell, public enyk: any, private motor: AnykSzabalymotor | null = null) { this.indexEpit(); }

  /** A szabálymotor utólag (a sablon betöltése után) is megadható – a gyorsítótárak ürülnek. */
  motorral(motor: AnykSzabalymotor | null) {
    this.motor = motor;
    this.helyTar.clear(); this.motorLapTar.clear();
    return this;
  }
  get formok() { return this.modell?.formok || []; }
  enykForm(formNev: string): any { return this.enyk?.formok?.[formNev.replace(/^Form_/, '')] || null; }

  private indexEpit() {
    this.xsdIdx = {};
    for (const f of this.formok) {
      const idx: Record<string, { m: NyMezo; lancok: string[] }> = {};
      const b = (n: any, lancok: string[]) => {
        if (n.tipus === 'mezo') { idx[n.kod] = { m: n, lancok }; return; }
        if (n.tipus === 'lanc') { (n.fej || []).forEach((e: any) => b(e, lancok)); n.elemek.forEach((e: any) => b(e, [...lancok, n.nev])); return; }
        (n.elemek || []).forEach((e: any) => b(e, lancok));
      };
      f.elemek.forEach((e: any) => b(e, []));
      this.xsdIdx[f.nev] = idx;
    }
    this.lapLancTar.clear();
  }
  /** Az XSD-mező egy fid mögött (a sortartományos fideket is feloldva). */
  xsd(formNev: string, fid: string) { return this.xsdIdx[formNev]?.[fid] || this.tartomanyMezo(formNev, fid)?.x || null; }
  mezo(formNev: string, fid: string): NyMezo | undefined { return this.xsd(formNev, fid)?.m; }
  /** Formonként az összes XSD-mező (a lapkép visszakeresésekhez). */
  mezok(formNev: string) { return this.xsdIdx[formNev] || {}; }

  /** Sortartomány: az ÁNYK külön fid-et ad a belső lánc minden sorának (pl. …D0521AA = az 520–522. tartomány 2. sora). */
  tartomanyMezo(formNev: string, fid: string): { x: { m: NyMezo; lancok: string[] }; sor: number } | null {
    const m = fid.match(/^(\d[A-Z](?:\d{4}|XXXX)D)(\d{4})([A-Z])A$/);
    if (!m) return null;
    const sor = Number(m[2]), osz = m[3].toLowerCase();
    for (const x of Object.values(this.xsdIdx[formNev] || {})) {
      const t = String(x.m.sor || '').match(/^(\d+)-(\d+)$/);
      if (!t || x.m.oszlop !== osz || !x.m.kod.startsWith(m[1])) continue;
      if (sor >= Number(t[1]) && sor <= Number(t[2])) return { x, sor: sor - Number(t[1]) };
    }
    return null;
  }
  /** A dinamikus lap lánca: az XSD-lánc, amelyben a lap XXXX-es mezői vannak. */
  lapLanca(formNev: string, oldal: any): string | null {
    const k = formNev + '|' + oldal.pid;
    if (this.lapLancTar.has(k)) return this.lapLancTar.get(k)!;
    const szam = new Map<string, number>();
    for (const e of oldal.elemek) if (e.t === 'mezo') {
      const x = this.xsd(formNev, e.fid);
      if (x?.lancok.length) szam.set(x.lancok[0], (szam.get(x.lancok[0]) || 0) + 1);
    }
    const l = [...szam.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    this.lapLancTar.set(k, oldal.dinamikus ? l : null);
    return oldal.dinamikus ? l : null;
  }
  fidHely(formNev: string, fid: string): AnykFidHely | null {
    const k = formNev + '|' + fid;
    if (!this.helyTar.has(k)) this.helyTar.set(k, this.motor?.fidHely(formNev, fid) || null);
    return this.helyTar.get(k)!;
  }
  /** A dinamikus lap külső lánca és lapnyi sorszáma a motor szerint. */
  motorLapLanc(formNev: string, oldal: any): { nev: string; laponkent: number } | null {
    const k = formNev + '|' + oldal.pid;
    if (this.motorLapTar.has(k)) return this.motorLapTar.get(k)!;
    let r: { nev: string; laponkent: number } | null = null;
    if (oldal.dinamikus) {
      for (const e of oldal.elemek) {
        if (e.t !== 'mezo') continue;
        const l = this.fidHely(formNev, e.fid)?.lancok?.[0];
        if (l?.laponkent) { r = { nev: l.nev, laponkent: l.laponkent }; break; }
      }
    }
    this.motorLapTar.set(k, r);
    return r;
  }
  /** Egy ENYK-mező helye a motor leképezése szerint (a dinamikus lap `din`-edik példányán); a hiányzó sorokat létrehozza. */
  private motorHely(formNev: string, peldany: any, din: number, fid: string): AnykHely | null {
    const h = this.fidHely(formNev, fid);
    if (!h || (h.csakElsoLap && din > 0)) return { obj: {}, kod: fid, seged: true };
    let o = peldany;
    for (const l of h.lancok) {
      if (l.fej) { const k = l.nev + '#fej'; if (!o[k] || typeof o[k] !== 'object') o[k] = {}; o = o[k]; continue; }
      const idx = (l.laponkent ? din * l.laponkent : 0) + (l.sor || 0);
      if (!Array.isArray(o[l.nev])) o[l.nev] = [];
      while (o[l.nev].length <= idx) o[l.nev].push({});
      o = o[l.nev][idx];
    }
    if (h.aux) { if (!o['#aux'] || typeof o['#aux'] !== 'object') o['#aux'] = {}; return { obj: o['#aux'], kod: fid, seged: true }; }
    return { obj: o, kod: h.kod };
  }
  /** Egy ÁNYK-mező helye az adatban (a hiányzó láncsorokat létrehozza). */
  hely(formNev: string, peldany: any, oldal: any, din: number, fid: string): AnykHely | null {
    if (!peldany) return null;
    if (this.motor) return this.motorHely(formNev, peldany, din, fid);
    const sorba = (obj: any, lanc: string, i: number) => {
      if (!Array.isArray(obj[lanc])) obj[lanc] = [];
      while (obj[lanc].length <= i) obj[lanc].push({});
      return obj[lanc][i];
    };
    const lapLanc = this.lapLanca(formNev, oldal);
    const kovet = (x: { m: NyMezo; lancok: string[] }, belsoSor: number) => {
      let o = peldany;
      x.lancok.forEach((l, i) => {
        const utolso = i === x.lancok.length - 1;
        o = sorba(o, l, l === lapLanc && i === 0 ? din : (utolso ? belsoSor : 0));
      });
      return { obj: o, kod: x.m.kod };
    };
    const x = this.xsdIdx[formNev]?.[fid];
    if (x) return kovet(x, 0);
    const t = this.tartomanyMezo(formNev, fid);
    if (t) return kovet(t.x, t.sor);
    // Csak az ÁNYK-ban létező segédmező (lapjelző, számított köztes érték): az XSD-ben nincs, tehát a
    // bevallásnak nem része – a lapképen nem jelenik meg (a szabálymotor a saját tárában kezeli).
    return { obj: {}, kod: fid, seged: true };
  }
  /** A dinamikus lap példányainak száma (a lánc sorai; legalább 1). */
  dinDb(formNev: string, peldany: any, oldal: any): number {
    const ml = this.motor ? this.motorLapLanc(formNev, oldal) : null;
    if (ml) return Array.isArray(peldany?.[ml.nev]) ? Math.max(1, Math.ceil(peldany[ml.nev].length / ml.laponkent)) : 1;
    const l = this.lapLanca(formNev, oldal);
    return l && Array.isArray(peldany?.[l]) ? Math.max(1, peldany[l].length) : 1;
  }
  /** Van-e a lapon (a lap adott példányán) kitöltött mező. */
  lapKitoltott(formNev: string, peldany: any, oldal: any, din: number): boolean {
    for (const e of oldal.elemek) if (e.t === 'mezo') {
      if (!this.xsd(formNev, e.fid)) continue;
      const h = this.hely(formNev, peldany, oldal, din, e.fid);
      const v = h?.obj[h.kod];
      if (v !== undefined && v !== null && v !== '' && v !== false) return true;
    }
    return false;
  }

  /**
   *  A NYOMTATANDÓ LAPOK – formonként, példányonként, dinamikus lapnál lappéldányonként.
   *
   *  `uresekNelkul` (alap): csak a kitöltött lapok kerülnek papírra, a részbizonylat főlapjával
   *  együtt (azon az azonosító adatok vannak, nélküle a köteg értelmezhetetlen). Kikapcsolva a
   *  nyomtatvány MINDEN lapja megy, üresen is – ahogy az ÁNYK „üres nyomtatvány" nyomtatása.
   */
  nyomtatandok(adat: any, { uresekNelkul = true } = {}): { formNev: string; peldany: any; oldal: any; din: number }[] {
    const out: { formNev: string; peldany: any; oldal: any; din: number }[] = [];
    for (const f of this.formok) {
      const ef = this.enykForm(f.nev);
      if (!ef) continue;
      const peldanyok: any[] = adat?.[f.nev]?.length ? adat[f.nev] : (uresekNelkul ? [] : [{}]);
      peldanyok.forEach((p: any) => {
        for (const o of ef.oldalak) {
          if (!o.nyomtathato) continue;
          const db = o.dinamikus ? this.dinDb(f.nev, p, o) : 1;
          for (let d = 0; d < db; d++) if (!uresekNelkul || o.fo || this.lapKitoltott(f.nev, p, o, d)) out.push({ formNev: f.nev, peldany: p, oldal: o, din: d });
        }
      });
    }
    return out;
  }
}
